import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Well, Drawer, EmptyState } from '@pushly/aqe/lib/components'
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, MessageOutlined } from '@ant-design/icons'
import { Alert, Flex, Input, Skeleton, Spin, Tag, Tooltip, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { getStatusIcon } from '@pushly/aqe/lib/components/badges/renderers/status-icons'
import * as randomstring from 'randomstring'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { AppService, AiGenerationDirectiveService, UserService } from '../../../services'
import { DomainDto } from '../../../dtos/domain'
import { getClassNames } from '../../../_utils/classnames'
import { simpleNotification } from '../../../_utils/utils'
import { antdStaticApi } from '../../../providers/antd-app-provider/antd-static-api'
import { VERTICAL_WELL_GAP } from '../../../theme/constants'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { BrandVoiceSummary, BrandVoiceSummarySkeleton } from './brand-voice-summary'
import { BrandVoiceStatus } from './brand-voice-status'
import { AiCompilingIndicator, BrandVoiceDecomposition, CompileState } from './brand-voice-decomposition'
import { applyReplacement, dimensionFor, groupSuggestionsByItem } from './promotion-suggestions'
import { locateSpan } from './span-locator'
import { PromotionModal, type IPromotionTarget } from './promotion-modal'
import { applyPromotionToCompiled, enforcedLimitsByAttribute } from './promotion-optimistic'
import { BRAND_VOICE_DOCS_URL } from './brand-voice-help-content'
import { DISPLAY_TIER_ORDER, tierCounts, type BrandVoiceDisplayTier } from './brand-voice-tier-visual'
import { BrandVoiceBridge } from './brand-voice-wiring'
import { BrandVoiceSendPreviewWell } from './brand-voice-send-preview'
import { useNotificationPermissions } from '../../../mobile-view/hooks/use-notification-permissions'

dayjs.extend(relativeTime)

const PLACEHOLDER =
    'e.g. Write like a trusted editor. Never use exclamation points. Keep titles under 60 characters. Favor curiosity over hype.'

interface IBrandVoiceEditor {
    domainId: number
    directive: AiGenerationDirective | null
    visible: boolean
    onClose: (e: any) => void
    onSaved: (directive: AiGenerationDirective) => void
}

const BrandVoiceEditor: FC<IBrandVoiceEditor> = (props) => {
    const appSvc = useService(AppService)
    const directiveSvc = useService(AiGenerationDirectiveService)

    // Textareas normalize \r\n/\r to \n internally for both their rendered value and
    // selectionStart/layout metrics — but the raw server body can still contain \r\n. If `body`
    // state kept those \r characters, every span offset computed against it would drift right
    // by one character per line break that precedes the match (the browser already dropped
    // that \r when laying the text out), compounding across paragraphs. Normalizing at the one
    // point raw server text enters state keeps our offsets and the browser's in agreement.
    const initialBody = (props.directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const initialCompiled = props.directive?.active_version?.compiled_rules ?? null

    const [body, setBody] = useState<string>(initialBody)
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [compileState, setCompileState] = useState<CompileState>(initialCompiled ? 'done' : 'idle')
    const [compiled, setCompiled] = useState<CompiledDirective | null>(initialCompiled)
    // The preview Save stores: the id of the compile `compiled` came from, for the text in
    // `lastCompiledBodyRef`. Null for rules loaded from the saved version, which only a fresh
    // compile replaces with something saveable.
    const [previewId, setPreviewId] = useState<string | null>(null)
    // Why the last save did not go through, shown under the text until the author edits it.
    const [saveProblem, setSaveProblem] = useState<
        { kind: 'stale' } | { kind: 'failed'; message: string | null } | null
    >(null)
    // Promotion suggestions for whatever `compiled` currently holds. Purely additive
    // to the editor: they are fetched after a compile lands, and a failure leaves them empty
    // rather than surfacing an error — losing a hint must never look like losing the analysis.
    const [suggestions, setSuggestions] = useState<DirectivePromotionSuggestion[]>([])
    // The domain's own measured p90 per dimension, carried alongside the suggestions so the
    // confirm step can retarget one onto another field or unit without another round trip.
    const [contentStats, setContentStats] = useState<{
        p90ByAttribute: Record<string, number>
        p50ByAttribute: Record<string, number>
        sampleSize: number
    }>({ p90ByAttribute: {}, p50ByAttribute: {}, sampleSize: 0 })
    // The statement whose promotion is being confirmed, or null. Null closes the modal.
    const [promotionTarget, setPromotionTarget] = useState<IPromotionTarget | null>(null)
    /**
     * A compile running over content that is ALREADY on screen. `compileState === 'compiling'`
     * is the other case — a first compile, with nothing to preserve — and that one replaces the
     * panel entirely.
     *
     *   'blocking'  the author clicked Analyze and is waiting for the answer. The rules stay
     *               visible under a loading overlay and the editor is locked, so the text cannot
     *               drift out from under a result that is about to describe it.
     *   'quiet'     WE started this, after a promotion. The author did not ask and may well keep
     *               working, so nothing is taken away — just a line saying a check is running.
     *
     * The distinction is who is waiting. Locking someone out of their own text is only
     * defensible when they asked for the thing they are being made to wait for.
     */
    const [busyMode, setBusyMode] = useState<'blocking' | 'quiet' | null>(null)

    // The body whatever `compiled` currently holds actually reflects (a persisted result on
    // load, or the last successful recompile) — lets the effect below tell "the author edited
    // the text" apart from "the component re-rendered with the same text", so opening the
    // drawer on a directive with a saved decomposition never fires a redundant compile call.
    const lastCompiledBodyRef = useRef<string | undefined>(initialCompiled ? initialBody : undefined)

    // The most recently ISSUED compile request's text — not the most recently RESOLVED one.
    // Debouncing only cancels a pending timer; it can't cancel a request already in flight, so
    // two compiles can legitimately overlap (e.g. the author keeps editing while a slow compile
    // of a long body is still running). Without this guard, whichever response happens to
    // resolve last wins regardless of which was issued last — a stale, shorter-body response
    // arriving after a newer one would silently overwrite it with rule text and highlight
    // offsets that no longer match what's on screen.
    const latestRequestedTextRef = useRef<string>('')

    // Read inside `runCompile` instead of closing over `compiled`, which would rebuild the
    // callback on every compile and re-arm the debounced background compile with a stale one.
    const compiledRef = useRef<CompiledDirective | null>(initialCompiled)
    useEffect(() => {
        compiledRef.current = compiled
    }, [compiled])

    const runCompile = useCallback(
        async (text: string, opts: { mode?: 'blocking' | 'quiet' } = {}) => {
            latestRequestedTextRef.current = text

            if (!text.trim()) {
                setCompiled(null)
                setCompileState('idle')
                setSuggestions([])
                lastCompiledBodyRef.current = undefined
                return
            }
            // With something already on screen, `compileState` stays 'done' so the decomposition
            // keeps rendering and the mode decides how the wait is presented. Submit is not
            // weakened by that: it also requires the text on screen to equal
            // `lastCompiledBodyRef`, which only a FINISHED compile sets.
            if (compiledRef.current) setBusyMode(opts.mode ?? 'blocking')
            else setCompileState('compiling')
            // Dropped up front, not on the response: they describe the OLD decomposition, and
            // leaving them on screen through a recompile would invite applying a rewrite whose
            // offsets belong to text that is already being replaced.
            setSuggestions([])
            setPromotionTarget(null)
            const res = await directiveSvc.compilePreview(props.domainId, text)

            // A newer request has since been issued (and will supersede this one on its own
            // resolution) — applying this now-stale result would only reintroduce the race.
            if (latestRequestedTextRef.current !== text) return

            setBusyMode(null)

            if (res.ok && res.data) {
                setCompiled(res.data.compiled_rules)
                setPreviewId(res.data.preview_id)
                setCompileState('done')
                lastCompiledBodyRef.current = text

                // Deliberately NOT awaited into the compile's own state transitions: the
                // decomposition is the answer the author asked for and must paint as soon as it
                // lands. Suggestions are an extra that arrives a moment later if it arrives.
                const suggestionRes = await directiveSvc.suggestPromotions(props.domainId, res.data.compiled_rules)
                if (latestRequestedTextRef.current !== text) return
                if (suggestionRes.ok && suggestionRes.data) {
                    setSuggestions(suggestionRes.data.suggestions ?? [])
                    setContentStats({
                        p90ByAttribute: suggestionRes.data.p90_by_attribute ?? {},
                        p50ByAttribute: suggestionRes.data.p50_by_attribute ?? {},
                        sampleSize: suggestionRes.data.sample_size ?? 0,
                    })
                }
            } else if (compiledRef.current) {
                // A failed compile must not replace content the author is reading with an error
                // panel — the previous rules are still true for the previous text.
                // `lastCompiledBodyRef` stays behind, so `isStale` lights the Analyze button and
                // the hint below the textarea already says the rules predate the current text.
                setCompileState('done')
            } else {
                setCompileState('error')
            }
        },
        [directiveSvc, props.domainId],
    )

    // Grouped by the statement each is about — an ambiguous-subject statement produces one
    // suggestion per plausible field, and they are alternatives the author chooses between
    // rather than a list of edits to make.
    const suggestionsByItem = useMemo(() => groupSuggestionsByItem(suggestions), [suggestions])

    // Limits the author has already written, by binding attribute — so the confirm step can
    // refuse to aim a promotion at a dimension their own text already sets.
    const enforcedByAttribute = useMemo(() => enforcedLimitsByAttribute(compiled), [compiled])

    /**
     * Rewrites the author's own text, which is the ONLY way a promotion can take effect:
     * compiled rules are re-derived from the body on every compile, so a binding introduced any
     * other way would evaporate on the next Analyze.
     *
     * Three things happen together, and the order matters to how it feels:
     *   1. the sentence is spliced into the text
     *   2. the decomposition is updated LOCALLY so the rule appears under Guaranteed at once
     *   3. the real compile is started in the background, and replaces (2) when it lands
     *
     * (2) is a prediction, not a result — see `applyPromotionToCompiled` for why it can be an
     * accurate one. It exists because the alternative was asking the author to click Analyze and
     * watch a spinner to see the outcome of a change they had already confirmed.
     *
     * (3) is what Save stores: until it lands, the text on screen has no preview of its own, so
     * Save stays disabled rather than storing rules nobody has seen compiled.
     */
    const handleConfirmPromotion = (
        replaces: DirectivePromotionSuggestion['replaces'],
        sentence: string,
        dimension: { field: 'title' | 'body'; unit: 'characters' | 'words'; value: number },
    ) => {
        // Offsets were computed against the text Analyze compiled, which is `body.trim()` — the
        // editor's own state keeps the author's surrounding whitespace, so the compiled body is
        // handed over to re-base them rather than left to a quote search to paper over.
        const located = locateSpan(body, replaces, lastCompiledBodyRef.current)
        const next = located && applyReplacement(body, replaces, sentence, lastCompiledBodyRef.current)
        if (!located || next == null) {
            // The modal disables its confirm when the span can't be located, so this is only
            // reachable if the text moved between opening it and confirming.
            simpleNotification(
                'warning',
                'This text has changed since you checked it — click “Generate rules” and try again.',
            )
            setPromotionTarget(null)
            return
        }

        const itemText = promotionTarget?.suggestions[0].item_text
        setBody(next)
        setIsDirty(true)
        setPromotionTarget(null)

        // Where the new sentence now sits — known exactly, since we just placed it. The
        // Guaranteed row it produces therefore links back to its own source text immediately,
        // rather than waiting for the compiler to re-locate a quote it wrote itself.
        const sentenceSpan = { start: located.start, end: located.start + sentence.length, quote: sentence }
        if (compiled && itemText) {
            setCompiled(
                applyPromotionToCompiled(
                    compiled,
                    itemText,
                    dimensionFor(dimension.field, dimension.unit),
                    dimension.value,
                    sentenceSpan,
                ),
            )
        }

        // Every remaining suggestion's offsets were computed against the body as it was BEFORE
        // this splice, so they have all just shifted. `locateSpan` re-finds them by quote,
        // but only the ones for OTHER statements — the alternatives that shared this statement
        // are now moot, since the sentence they were about no longer exists.
        setSuggestions((current) => current.filter((s) => s.item_text !== itemText))

        scheduleBackgroundCompile(next.trim())
    }

    /**
     * Starts the real compile behind the local prediction, coalescing a run of promotions into
     * one request.
     *
     * Promoting three suggestions in a row is a normal thing to do, and firing a ten-second
     * compile after each would burn two of them on text that was superseded before they
     * returned. The delay is short enough to be invisible when only one promotion happens.
     */
    const backgroundCompileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scheduleBackgroundCompile = (text: string) => {
        if (backgroundCompileTimerRef.current) clearTimeout(backgroundCompileTimerRef.current)
        backgroundCompileTimerRef.current = setTimeout(() => runCompile(text, { mode: 'quiet' }), 1200)
    }

    useEffect(() => {
        return () => {
            if (backgroundCompileTimerRef.current) clearTimeout(backgroundCompileTimerRef.current)
        }
    }, [])

    // Explicit "Analyze" trigger, not a debounced auto-compile: firing a compile
    // after every pause in typing meant any hesitation mid-sentence kicked off a request
    // against still-incomplete text — annoying in itself, and the source of most of the
    // "stale result overwritten by a slower one" races `latestRequestedTextRef` above exists
    // to guard against. The author decides when the text is actually ready to analyze.
    // `isStale` is what the Analyze button and decomposition below key off of to signal that
    // the rules on screen no longer reflect what's currently written.
    // Compared against `body.trim()`, not raw `body`: `runCompile` is always called with
    // trimmed text below (see its call sites), matching exactly what `handleSubmit` persists
    // as the directive's body. Comparing untrimmed `body` here would flag surrounding
    // whitespace alone as "changed since last analyzed", and — more importantly — a save names
    // the preview of one exact string: the server refuses it for any other text, and the
    // `sources` spans in its rules are character offsets into that string.
    const isStale = body.trim() !== '' && body.trim() !== lastCompiledBodyRef.current

    // A saved Brand Voice whose version has no rules yet can be saved again as it is, once its
    // text has a preview: that stores the rules it has been missing.
    const needsRules = !!props.directive && !initialCompiled

    const handleSubmit = async () => {
        const trimmed = body.trim()
        if (!trimmed) {
            simpleNotification('error', 'Add some text before saving your Brand Voice.')
            return
        }
        if (!compiled || !previewId) {
            // `disableSubmit` below should prevent reaching here without a completed, current
            // compile — defensive guard, not the primary gate.
            simpleNotification('error', 'Still checking your rules — try saving again in a moment.')
            return
        }

        setSaving(true)
        setSaveProblem(null)
        try {
            // One write: the text and the rules of its preview, or nothing at all.
            const result = await directiveSvc.saveBrandVoice(
                props.domainId,
                props.directive?.id ?? null,
                trimmed,
                previewId,
            )

            if (result.kind === 'saved') {
                simpleNotification('success', 'Your Brand Voice has been saved.')
                props.onSaved(result.directive)
            } else if (result.kind === 'stale') {
                // The preview expired, or someone else saved since. Nothing changed; show the
                // rules as they compile now, for the author to review before saving again.
                setSaveProblem({ kind: 'stale' })
                runCompile(trimmed)
            } else {
                setSaveProblem({ kind: 'failed', message: result.message })
            }
        } finally {
            setSaving(false)
        }
    }

    const compiling = compileState === 'compiling' || !!busyMode

    // Never enabled mid-compile or against stale or errored text: Submit only becomes available once `compiled`
    // reflects a finished compile of the exact text on screen, so what gets saved is what was reviewed. Not gated on
    // `saving`: the button's own loading state already ignores a second click while the save runs. Also off while a
    // compile runs, including the refresh after a refused save, since saving the previous preview would only be
    // refused again.
    const submitDisabled =
        !(isDirty || needsRules) ||
        compileState !== 'done' ||
        !!busyMode ||
        !previewId ||
        body.trim() !== lastCompiledBodyRef.current

    // What the footer says about where the author is: the next step, or why Submit is waiting.
    const footerHint = !body.trim()
        ? 'Describe your Brand Voice to generate its rules.'
        : compiling
        ? 'Generating rules from your text…'
        : isStale
        ? 'Generate rules to review what changed before submitting.'
        : compileState === 'error'
        ? 'The rules could not be generated. Try again.'
        : submitDisabled
        ? 'No changes to submit.'
        : 'Rules match your text.'

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-brand-voice-editor')}
            title="Edit your Brand Voice"
            placement="right"
            closable={true}
            // The footer carries the whole flow: Cancel, Generate rules, and Submit, with the primary emphasis on
            // whichever is the next step, and a line saying why Submit is waiting when it is.
            footer={
                <Flex align="center" gap={8} className={getClassNames('brand-voice-editor-footer')}>
                    <Typography.Text type="secondary" className={getClassNames('brand-voice-editor-hint')}>
                        <InfoCircleOutlined /> {footerHint}
                    </Typography.Text>
                    <span style={{ flex: 1 }} />
                    <Button onClick={() => props.onClose(undefined)}>Cancel</Button>
                    <Button
                        type={isStale || compileState === 'error' ? 'primary' : 'default'}
                        loading={compiling}
                        // Also blocked while saving: a recompile mid-save could land after the save and make
                        // `compiled` disagree with what was just written.
                        disabled={compiling || !(isStale || compileState === 'error') || saving}
                        // Trimmed to match exactly what handleSubmit saves; see `isStale`. With rules already on
                        // screen, the compile runs over them rather than replacing them with a spinner, since the
                        // previous rules stay true for the previous text while it runs.
                        onClick={() => runCompile(body.trim())}
                    >
                        Generate rules
                    </Button>
                    <Button
                        type={submitDisabled ? 'default' : 'primary'}
                        disabled={submitDisabled}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Flex>
            }
            onClose={props.onClose}
            open={props.visible}
            // Narrow enough to read as a form: the two sections stack, so neither needs half of a wide drawer.
            size={760}
        >
            <Flex vertical gap={16} className={getClassNames('brand-voice-editor-body')}>
                <Typography.Text className={getClassNames('brand-voice-intro')}>
                    Describe how you want AI-written content to sound. Rules we can check automatically are enforced
                    every time; everything else guides the tone. It never touches content you write yourself.{' '}
                    <Typography.Link href={BRAND_VOICE_DOCS_URL} target="_blank" rel="noopener noreferrer">
                        Learn more
                    </Typography.Link>
                </Typography.Text>

                {/* Two wells, stacked: the text first, since the rules come from it, then the rules. The drawer is the
                    one scroll; the text field scrolls inside itself like any field once it is longer than its box. */}
                <Well
                    className={getClassNames('brand-voice-editor-well')}
                    title="What you wrote"
                    titleSize="sm"
                    padding="small"
                    shadow={true}
                    footerMode="hidden"
                    actions={
                        isStale && compiled ? (
                            <Typography.Text className={getClassNames('brand-voice-editor-stale')}>
                                <DraftIcon /> Edited since rules were generated
                            </Typography.Text>
                        ) : undefined
                    }
                >
                    <Flex vertical gap={8}>
                        <Input.TextArea
                            // Locked while the author waits on a compile they asked for: editing mid-compile means
                            // the result lands describing text that no longer exists. A quiet refresh after a
                            // promotion never locks, since nobody asked for it and they may still be working.
                            disabled={compileState === 'compiling' || busyMode === 'blocking'}
                            value={body}
                            onChange={(e) => {
                                // Defensive: the DOM's textarea already normalizes typed and pasted input to \n.
                                setBody(e.target.value.replace(/\r\n?/g, '\n'))
                                setIsDirty(true)
                                setSaveProblem(null)
                            }}
                            placeholder={PLACEHOLDER}
                            aria-label="What you wrote"
                            className={getClassNames('brand-voice-editor-text')}
                        />
                        {saveProblem?.kind === 'stale' && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Nothing was saved: your rules changed since you reviewed them. We’ve refreshed them below. Review them, then save again."
                            />
                        )}
                        {saveProblem?.kind === 'failed' && (
                            <Alert
                                type="error"
                                showIcon
                                message={
                                    saveProblem.message ??
                                    'We couldn’t save your Brand Voice. Nothing was changed. Try again.'
                                }
                            />
                        )}
                    </Flex>
                </Well>

                <Well
                    className={getClassNames('brand-voice-editor-well')}
                    title="What we compiled"
                    titleSize="sm"
                    padding="small"
                    shadow={true}
                    footerMode="hidden"
                >
                    {/* antd caps a wrapping Spin's indicator to the top of the box, so on a long list it stays in
                        view rather than centering on the full height. */}
                    <Spin
                        spinning={busyMode === 'blocking'}
                        wrapperClassName={getClassNames('bvd-overlay')}
                        indicator={<AiCompilingIndicator active={busyMode === 'blocking'} />}
                    >
                        <BrandVoiceDecomposition
                            state={compileState}
                            compiled={compiled}
                            hasText={!!body.trim()}
                            refreshing={busyMode === 'quiet'}
                            onRetry={() => runCompile(body.trim())}
                            suggestionsByItem={suggestionsByItem}
                            onPromote={(group) =>
                                setPromotionTarget({
                                    suggestions: group,
                                    p90ByAttribute: contentStats.p90ByAttribute,
                                    p50ByAttribute: contentStats.p50ByAttribute,
                                    sampleSize: contentStats.sampleSize,
                                    enforcedByAttribute,
                                })
                            }
                        />
                    </Spin>
                </Well>
            </Flex>

            <PromotionModal
                target={promotionTarget}
                body={body}
                compiledBody={lastCompiledBodyRef.current}
                onCancel={() => setPromotionTarget(null)}
                onConfirm={handleConfirmPromotion}
            />
        </Drawer>
    )
}

// The platform's canonical Draft icon: text edited since its rules were generated is a draft of them.
const DraftIcon = getStatusIcon('DRAFT')

interface IBrandVoiceWell {
    /** Not just an id: the send preview draws the notification with the domain's name, icon and badge. */
    domain: DomainDto
    canEdit: boolean
}

export const BrandVoiceWell: FC<IBrandVoiceWell> = (props) => {
    const directiveSvc = useService(AiGenerationDirectiveService)
    const userSvc = useService(UserService)
    const { canCreate: canGenerate } = useNotificationPermissions()

    const [loading, setLoading] = useState(true)
    const [directive, setDirective] = useState<AiGenerationDirective | null>(null)
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')
    // Resolved separately from the directive fetch: `updated_by` on the wire is only a user id,
    // and there is no org-users store already loaded to look it up against — see `fetchUserById`.
    const [updatedByName, setUpdatedByName] = useState<string | null>(null)
    // null until the author picks a tag themselves — see `activeTier` below for the fallback
    // this defers to until then.
    const [selectedTier, setSelectedTier] = useState<BrandVoiceDisplayTier | null>(null)

    // `cancelled` is checked after the await: switching domains starts a second fetch while the
    // first is still open, and without this the slower response wins whichever domain it belongs
    // to — showing one domain's Brand Voice under another's heading.
    const loadBrandVoice = async (isCancelled: () => boolean = () => false) => {
        setLoading(true)
        const res = await directiveSvc.fetchBrandVoice(props.domain.id)
        if (isCancelled()) return
        if (res.ok) {
            setDirective(res.data ?? null)
        }
        setLoading(false)
    }

    useEffect(() => {
        let cancelled = false
        loadBrandVoice(() => cancelled)
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.domain.id])

    useEffect(() => {
        let cancelled = false
        const updatedBy = directive?.updated_by
        if (!updatedBy) {
            setUpdatedByName(null)
            return
        }
        userSvc.fetchUserById(updatedBy, { muteErrors: true }).then((user) => {
            if (!cancelled) setUpdatedByName(user?.name ?? null)
        })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directive?.updated_by])

    const body = (directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const compiledRules = directive?.active_version?.compiled_rules ?? null
    const counts = useMemo(() => tierCounts(compiledRules), [compiledRules])
    const updatedAt = directive?.updated_at

    // Soft-deletes the directive; generation then falls back to the default style. The well goes
    // back to its "No Brand Voice set yet" state without a refetch, since there is nothing to fetch.
    // A failure is already reported by the request layer's own error message, so the Brand Voice is
    // simply left as it was.
    const removeBrandVoice = async () => {
        if (!directive) return

        const res = await directiveSvc.destroy(props.domain.id, directive.id)
        if (!res.ok) return

        setDirective(null)
        setSelectedTier(null)
        setShowDrawer(false)
        simpleNotification('success', 'Brand Voice removed.')
    }

    // Opened through `antdStaticApi` rather than the Button action's own `confirm` option: that one
    // uses antd's static `Modal.confirm`, which sits outside the app's ConfigProvider and so ignores
    // our theme colors. `onOk` returns the promise so the dialog keeps its confirm button loading
    // until the request settles.
    const confirmRemoveBrandVoice = () => {
        antdStaticApi.modal.confirm({
            title: 'Remove Brand Voice?',
            content:
                'AI-written content for this domain will go back to our default style. You can set up a new Brand Voice at any time.',
            okText: 'Remove',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: removeBrandVoice,
        })
    }

    // Live is what the directives call answered: the domain has a Brand Voice, so generation applies it. With none,
    // generation uses the default style.
    const live = !!directive

    // The tag the author clicked, if any, else whichever tier actually has something in it —
    // a directive with no guarantees should not default to an empty Guaranteed pane.
    const activeTier: BrandVoiceDisplayTier =
        selectedTier ?? DISPLAY_TIER_ORDER.find((t) => counts[t] > 0) ?? 'guaranteed'

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('bvw')}>
            {/* One card for the Brand Voice: what it is, its status and its actions in the header, then the text and
                the rules. The edit metadata sits beside the Edit button it describes rather than in a card above. */}
            <Well
                className="nested"
                title="Brand Voice"
                actions={
                    <Flex align="center" gap={8}>
                        {loading ? (
                            // Held at the tag's size, so the header does not shift when the status lands.
                            <Skeleton.Button active={true} size="small" style={{ width: 64 }} />
                        ) : (
                            <BrandVoiceStatus state={live ? 'live' : 'notSetUp'} />
                        )}
                        <Button
                            size="small"
                            onClick={() => {
                                setDrawerKey(randomstring.generate())
                                setShowDrawer(true)
                            }}
                            disabled={!props.canEdit || loading}
                            // Only once there is a Brand Voice to remove; with none, Edit stands alone.
                            actions={
                                directive
                                    ? [
                                          {
                                              key: 'remove',
                                              text: 'Remove Brand Voice',
                                              icon: <DeleteOutlined />,
                                              danger: true,
                                              onClick: confirmRemoveBrandVoice,
                                          },
                                      ]
                                    : undefined
                            }
                        >
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    </Flex>
                }
                footerMode="hidden"
                // A floor, not a locked height: stacked in a narrow card the columns need more room, and a locked
                // height clipped the second column out of sight. `overflow: hidden` keeps the well from showing a
                // scrollbar of its own; the rules column scrolls instead.
                bodyStyle={{ minHeight: 340, overflow: 'hidden' }}
                shadow={true}
            >
                {/* While loading, the card is drawn as it will land: the words that do not depend on the Brand Voice
                    as they are, and placeholders where its text, rules and edit line will be, at their real sizes. */}
                <Flex vertical gap={16}>
                    <Flex vertical gap={4}>
                        <Typography.Paragraph className="ai-muted-text" style={{ marginBottom: 0 }}>
                            Describe how you want AI-written content to sound. Rules we can check automatically are
                            enforced every time; everything else guides the tone.{' '}
                            <Typography.Link href={BRAND_VOICE_DOCS_URL} target="_blank" rel="noopener noreferrer">
                                Learn more
                            </Typography.Link>
                        </Typography.Paragraph>

                        {loading ? (
                            <Skeleton.Input active={true} size="small" style={{ width: 180, height: 14 }} />
                        ) : (
                            updatedAt && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    Edited {dayjs(updatedAt).fromNow()}
                                    {updatedByName ? ` by ${updatedByName}` : ''}
                                </Typography.Text>
                            )
                        )}
                    </Flex>

                    {loading ? (
                        <BrandVoiceSummarySkeleton />
                    ) : body ? (
                        <BrandVoiceSummary
                            body={body}
                            compiled={compiledRules}
                            activeTier={activeTier}
                            onTierChange={setSelectedTier}
                        />
                    ) : (
                        <Flex align="center" justify="center" style={{ minHeight: 220 }}>
                            <EmptyState
                                size="small"
                                icon={<MessageOutlined />}
                                iconVariant="chip"
                                title="No Brand Voice set yet"
                                description="Until you do, AI-written content uses our default style."
                                actions={
                                    <Button
                                        size="small"
                                        type="primary"
                                        disabled={!props.canEdit}
                                        onClick={() => {
                                            setDrawerKey(randomstring.generate())
                                            setShowDrawer(true)
                                        }}
                                    >
                                        <EditOutlined />
                                        <span>Set up Brand Voice</span>
                                    </Button>
                                }
                            />
                        </Flex>
                    )}
                </Flex>

                <BrandVoiceEditor
                    key={drawerKey}
                    domainId={props.domain.id}
                    directive={directive}
                    visible={showDrawer}
                    onClose={() => setShowDrawer(false)}
                    onSaved={(updated) => {
                        setDirective(updated)
                        setShowDrawer(false)
                    }}
                />
            </Well>

            <BrandVoiceBridge key={props.domain.id} domainId={props.domain.id}>
                <BrandVoiceSendPreviewWell
                    domain={props.domain}
                    hasDirective={!!body}
                    directiveLoading={loading}
                    canGenerate={canGenerate}
                />
            </BrandVoiceBridge>
        </Flex>
    )
}
